import { useMemo } from 'react'
import Accordion from '@dustin-web/microsite-components/src/accordion'
import {
  useLayout,
  useQueryState,
  useTranslations,
} from '@dustin-web/microsite-components/src/hooks'
import {
  BOOLEAN_FACET_NAME,
  FacetGroup,
  FacetGroupHeader,
  FacetGroupMultiple,
  FacetGroupRange,
  FacetType,
} from '.'

type Props = {
  facets: FacetGroup[]
}

const buildBooleanFacet = (booleanFacets: FacetGroup[], translate: (key: string) => string) => {
  const booleanFacetsValues = booleanFacets
    .map(f => f.values)
    .reduce((acc, values, i) => {
      const originalFacet = booleanFacets[i]
      const val = values.find(v => v.name === 'true')
      if (!val) {
        return acc
      }
      const newVal = {
        ...val,
        name: originalFacet.nameInUrl,
      }
      return [...acc, newVal]
    }, [])

  return {
    displayName: translate(`FacetGroup_${BOOLEAN_FACET_NAME}`),
    displayEnglishName: BOOLEAN_FACET_NAME,
    facetOpened: true,
    isTranslated: false,
    name: BOOLEAN_FACET_NAME,
    nameInUrl: BOOLEAN_FACET_NAME,
    type: FacetType.Multiple,
    unitText: '',
    values: booleanFacetsValues,
  }
}

const buildFacetGroups = (facets: FacetGroup[], translate: (key: string) => string) => {
  const openFacets = facets.filter(f => f.facetOpened && !(f.type === FacetType.Boolean))
  const closedFacets = facets.filter(f => !f.facetOpened && !(f.type === FacetType.Boolean))
  const booleanFacets = facets.filter(f => f.type === FacetType.Boolean)
  const booleanFacet = buildBooleanFacet(booleanFacets, translate)
  const facetGroups = [booleanFacet, ...openFacets, ...closedFacets]

  return facetGroups
}

export const Filters = ({ facets }: Props) => {
  const { translate } = useTranslations()
  const { getActiveFilterValue, queryState } = useQueryState()
  const { responsive } = useLayout()

  const alwaysExpandedFacetGroups = ['showonly', 'manufacturer', 'price']
  const activeFacetGroups = queryState.activeFilters.map(f => f.name)

  if (!facets) {
    throw new Error('Not found')
  }

  const facetGroups = useMemo(() => buildFacetGroups(facets, translate), [facets])
  const shouldExpandFacetGroup = (
    groupName: string,
    activeGroups: string[],
    alwaysExpandedGroups: string[]
  ) => {
    return alwaysExpandedGroups.includes(groupName) || activeGroups.includes(groupName)
  }

  return (
    <>
      {facetGroups.map((facetGroup, key) => {
        const isFacetExpanded = shouldExpandFacetGroup(
          facetGroup.nameInUrl,
          activeFacetGroups,
          alwaysExpandedFacetGroups
        )

        const selectedFacetsCount = getActiveFilterValue(facetGroup.nameInUrl).length
        const FacetGroupRenderer =
          facetGroup.type === FacetType.Range ? FacetGroupRange : FacetGroupMultiple
        const showFacetGroup =
          selectedFacetsCount ||
          facetGroup.values.some(f => {
            return f.hits > 0
          })
        const hasSelectedFacets = selectedFacetsCount > 0

        return (
          showFacetGroup && (
            <div key={facetGroup.name + key} className="c-filter-facet-group">
              <Accordion
                title={
                  <FacetGroupHeader
                    facetGroup={facetGroup}
                    hasSelectedFacets={hasSelectedFacets}
                    selectedFacetsCount={selectedFacetsCount}
                  />
                }
                slim
                noBackground
                extState
                extOpen={isFacetExpanded}
                sticky={false}>
                <FacetGroupRenderer
                  facetGroup={facetGroup}
                  className={!responsive.isSmallScreen ? 'u-pt-8' : ''}
                />
              </Accordion>
            </div>
          )
        )
      })}
    </>
  )
}
